import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { Mail, User, Calendar, Shield, Activity, Eye, EyeOff, LogOut, Smartphone, HelpCircle } from 'lucide-react';
import { TwoFactorAuthModal } from './TwoFactorAuthModal';
import { SecurityQuestionsModal } from './SecurityQuestionsModal';
import { ConsentPreferencesSection } from './ConsentPreferencesSection';

interface Profile {
  full_name: string;
  home_country?: string;
  preferred_currency: string;
  dietary_preferences?: string[];
  mobile_carrier?: string;
}

export function AccountSection() {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const { subscription } = useSubscription();
  const [profile, setProfile] = useState<Profile>({
    full_name: '',
    preferred_currency: 'USD',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [accountStats, setAccountStats] = useState({
    totalTrips: 0,
    countriesVisited: 0,
    daysSinceJoin: 0,
  });
  const [recentActivity, setRecentActivity] = useState([
    { action: 'Loading recent activity...', date: new Date().toISOString().split('T')[0], time: '...' },
  ]);
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [securityQuestionsModalOpen, setSecurityQuestionsModalOpen] = useState(false);
  const [hasSecurityQuestions, setHasSecurityQuestions] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchAccountStats();
      fetchRecentActivity();
      checkMFAStatus();
      checkSecurityQuestions();
    }
  }, [user]);

  const checkMFAStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      
      setTwoFactorEnabled(
        data?.totp ? data.totp.some(factor => factor.status === 'verified') : false
      );
    } catch (error) {
      console.error('Error checking MFA status:', error);
    }
  };

  const checkSecurityQuestions = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('security_questions')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      setHasSecurityQuestions(!!data);
    } catch (error) {
      console.error('Error checking security questions:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile({
          full_name: data.full_name || '',
          home_country: data.home_country,
          preferred_currency: data.preferred_currency || 'USD',
          dietary_preferences: data.dietary_preferences || [],
          mobile_carrier: data.mobile_carrier,
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchAccountStats = async () => {
    if (!user) return;

    try {
      // Fetch total trips
      const { data: trips, error: tripsError } = await supabase
        .from('trips')
        .select('id, destination_country, created_at')
        .eq('user_id', user.id);

      if (tripsError) throw tripsError;

      // Calculate unique countries visited
      const uniqueCountries = new Set(trips?.map(trip => trip.destination_country) || []);
      
      // Calculate days since join (from user's created_at)
      const { data: authUser } = await supabase.auth.getUser();
      const joinDate = authUser.user?.created_at;
      const daysSinceJoin = joinDate ? 
        Math.floor((new Date().getTime() - new Date(joinDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;

      setAccountStats({
        totalTrips: trips?.length || 0,
        countriesVisited: uniqueCountries.size,
        daysSinceJoin,
      });
    } catch (error) {
      console.error('Error fetching account stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    if (!user) return;

    try {
      // Fetch recent trips, expenses, and notifications for activity
      const { data: recentTrips } = await supabase
        .from('trips')
        .select('title, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(2);

      const { data: recentExpenses } = await supabase
        .from('expenses')
        .select('description, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const activities = [];

      // Add recent trips
      if (recentTrips) {
        recentTrips.forEach(trip => {
          activities.push({
            action: `Created trip: ${trip.title}`,
            date: trip.created_at.split('T')[0],
            time: new Date(trip.created_at).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit', 
              hour12: true 
            })
          });
        });
      }

      // Add recent expenses
      if (recentExpenses) {
        recentExpenses.forEach(expense => {
          activities.push({
            action: `Added expense: ${expense.description || 'Travel expense'}`,
            date: expense.created_at.split('T')[0],
            time: new Date(expense.created_at).toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit', 
              hour12: true 
            })
          });
        });
      }

      // Sort by date and take most recent
      activities.sort((a, b) => new Date(`${b.date} ${b.time}`).getTime() - new Date(`${a.date} ${a.time}`).getTime());
      
      if (activities.length === 0) {
        activities.push({
          action: 'Welcome to OdyVoy! Start by creating your first trip.',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        });
      }

      setRecentActivity(activities.slice(0, 3));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          email: user.email!,
          full_name: profile.full_name,
          home_country: profile.home_country,
          preferred_currency: profile.preferred_currency,
          dietary_preferences: profile.dietary_preferences,
          mobile_carrier: profile.mobile_carrier,
        });

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your account information has been saved successfully.",
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "New passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
      setShowPasswordForm(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      toast({
        title: "Error",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Country to currency mapping
  const countryCurrencyMap: { [key: string]: string } = {
    'United States': 'USD',
    'Canada': 'CAD',
    'United Kingdom': 'GBP',
    'Eurozone': 'EUR',
    'Germany': 'EUR',
    'France': 'EUR',
    'Italy': 'EUR',
    'Spain': 'EUR',
    'Netherlands': 'EUR',
    'Belgium': 'EUR',
    'Japan': 'JPY',
    'China': 'CNY',
    'India': 'INR',
    'Australia': 'AUD',
    'New Zealand': 'NZD',
    'South Korea': 'KRW',
    'Singapore': 'SGD',
    'Hong Kong': 'HKD',
    'Switzerland': 'CHF',
    'Norway': 'NOK',
    'Sweden': 'SEK',
    'Denmark': 'DKK',
    'Brazil': 'BRL',
    'Mexico': 'MXN',
    'South Africa': 'ZAR',
    'Russia': 'RUB',
    'Turkey': 'TRY',
    'Thailand': 'THB',
    'Malaysia': 'MYR',
    'Philippines': 'PHP',
    'Indonesia': 'IDR',
    'Vietnam': 'VND',
    'Poland': 'PLN',
    'Czech Republic': 'CZK',
    'Hungary': 'HUF',
    'Israel': 'ILS',
    'Egypt': 'EGP',
    'Saudi Arabia': 'SAR',
    'UAE': 'AED',
    'Kuwait': 'KWD',
    'Qatar': 'QAR',
    'Chile': 'CLP',
    'Argentina': 'ARS',
    'Colombia': 'COP',
    'Peru': 'PEN',
    'Iceland': 'ISK'
  };

  const handleCountryChange = (country: string) => {
    const currency = countryCurrencyMap[country] || 'USD';
    setProfile({ 
      ...profile, 
      home_country: country,
      preferred_currency: currency
    });
  };

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl -z-10" />
        <Card className="border-0 bg-transparent shadow-none">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div className="flex-1 space-y-3 sm:space-y-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                    {profile.full_name || 'Welcome, Voyager!'}
                  </h1>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <p className="text-sm sm:text-base text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <Badge variant="secondary" className="self-start sm:ml-2">
                      {subscription.subscription_tier ? 
                        subscription.subscription_tier.replace('_', ' ').split(' ').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ') : 
                        'Economy'
                      }
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3 sm:gap-6">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-primary">{accountStats.totalTrips}</div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Trips</p>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-primary">{accountStats.countriesVisited}</div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Countries</p>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-primary">{accountStats.daysSinceJoin}</div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Days</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Settings */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Settings
            </CardTitle>
            <CardDescription>
              Update your personal information and preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Display Name</Label>
                <Input
                  id="full_name"
                  value={profile.full_name}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  disabled={!isEditing}
                  placeholder="Enter your display name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed from this panel
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              {isEditing ? (
                <>
                  <Button onClick={handleSaveProfile} disabled={loading} className="w-full min-h-[44px]">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)} className="w-full min-h-[44px]">
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)} className="w-full min-h-[44px]">
                  Edit Profile
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Security & Activity */}
        <div className="space-y-6">
          {/* Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security & Privacy
              </CardTitle>
              <CardDescription>
                Manage your account security and privacy settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg bg-card/50">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                    <p className="font-medium">Password Protection</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Keep your account secure</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="min-h-[40px] w-full sm:w-auto"
                >
                  {showPasswordForm ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Hide
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Change
                    </>
                  )}
                </Button>
              </div>

              {showPasswordForm && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <Button onClick={handlePasswordChange} disabled={loading} className="w-full min-h-[44px]">
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              )}

              <Separator />

              {/* Two-Factor Authentication */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg bg-card/50">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${twoFactorEnabled ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                    <p className="font-medium">Two-Factor Authentication</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {twoFactorEnabled ? 'Extra security enabled' : 'Add an extra layer'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTwoFactorModalOpen(true)}
                  className="min-h-[40px] w-full sm:w-auto"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  {twoFactorEnabled ? 'Manage' : 'Enable'}
                </Button>
              </div>

              <Separator />

              {/* Security Questions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg bg-card/50">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${hasSecurityQuestions ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                    <p className="font-medium">Security Questions</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {hasSecurityQuestions ? 'Recovery method active' : 'Recovery option'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSecurityQuestionsModalOpen(true)}
                  className="min-h-[40px] w-full sm:w-auto"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  {hasSecurityQuestions ? 'Manage' : 'Set Up'}
                </Button>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium text-sm">Connected Accounts</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-card/30">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Email Authentication</span>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-700">Connected</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>
                Your latest actions and app usage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg bg-card/30 hover:bg-card/50 transition-colors">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <div className="space-y-1 flex-1">
                      <p className="text-sm font-medium">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(activity.date)} at {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Consent Preferences - Full Width */}
      <ConsentPreferencesSection />

      {/* Bottom Actions */}
      <div className="flex flex-col gap-4 pt-6 border-t">
        <div className="flex flex-wrap gap-3 text-sm justify-center sm:justify-start">
          <a href="/terms" className="text-primary hover:underline font-medium">
            Terms of Service
          </a>
          <span className="text-muted-foreground">•</span>
          <a href="/privacy" className="text-primary hover:underline font-medium">
            Privacy Policy
          </a>
        </div>
        <Button 
          variant="outline" 
          onClick={handleSignOut}
          className="w-full min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>

      <TwoFactorAuthModal
        open={twoFactorModalOpen}
        onOpenChange={setTwoFactorModalOpen}
        isEnabled={twoFactorEnabled}
        onStatusChange={checkMFAStatus}
      />

      <SecurityQuestionsModal
        open={securityQuestionsModalOpen}
        onOpenChange={setSecurityQuestionsModalOpen}
        hasQuestions={hasSecurityQuestions}
        onStatusChange={checkSecurityQuestions}
      />
    </div>
  );
}